import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, GitCompareArrows, EyeOff, Eye, Trash2, X, Images, ScanEye, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import { adminGetCulling, adminStartCulling, adminClearCulling, adminCullEstimate, adminSetImageVisibility, adminBulkDeleteImages } from '@/lib/api';
import { CULL } from '@/constants/testIds';
import { CullRunPanel } from '@/components/culling/CullRunPanel';
import { CullGrid } from '@/components/culling/CullGrid';
import { DuplicateGroups } from '@/components/culling/DuplicateGroups';
import { CullCompare } from '@/components/culling/CullCompare';
import { FILTERS, matchesFilter, sortImages, isFlagged } from '@/components/culling/cullUtils';
import { SkeletonList } from '@/components/SkeletonList';
import { readBoolPref, writeBoolPref } from '@/lib/prefs';

const AI_PREF_KEY = 'lxra.cull.aiEyes';

export default function CullReview() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [aiEyes, setAiEyes] = useState(() => readBoolPref(AI_PREF_KEY, true));
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('order');
  const [selected, setSelected] = useState(() => new Set());
  const [compareIds, setCompareIds] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGetCulling(slug);
      setData(res);
      setLoadError(null);
      return res;
    } catch (err) {
      setLoadError(err?.response?.data?.detail || 'Could not load this gallery.');
      return null;
    }
  }, [slug]);

  useEffect(() => {
    load();
    adminCullEstimate(slug).then(setEstimate).catch(() => setEstimate(null));
  }, [slug, load]);

  // Poll while a run is in flight.
  const running = data?.run?.status === 'running';
  useEffect(() => {
    if (!running) { clearInterval(pollRef.current); return undefined; }
    pollRef.current = setInterval(async () => {
      const res = await load();
      if (res && res.run?.status !== 'running') {
        clearInterval(pollRef.current);
        if (res.run?.status === 'done') {
          const s = res.run.summary || {};
          toast.success(`Culling done — ${s.blurry || 0} blurry, ${s.eyes_closed || 0} eyes closed, ${s.duplicate_groups || 0} duplicate group${s.duplicate_groups === 1 ? '' : 's'}.`);
        } else if (res.run?.status === 'error') {
          toast.error(res.run.error || 'The culling pass failed.');
        }
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [running, load]);

  const images = useMemo(() => data?.images || [], [data?.images]);
  const imagesById = useMemo(() => Object.fromEntries(images.map((i) => [i.id, i])), [images]);
  const visible = useMemo(() => sortImages(images.filter((i) => matchesFilter(i, filter)), sort), [images, filter, sort]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, images.filter((i) => matchesFilter(i, f.key)).length])), [images]);
  const groups = data?.run?.groups || [];
  const selectedList = useMemo(() => [...selected].map((id) => imagesById[id]).filter(Boolean), [selected, imagesById]);
  const compareList = useMemo(() => (compareIds || []).map((id) => imagesById[id]).filter(Boolean), [compareIds, imagesById]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectMany = (ids) => setSelected((prev) => { const n = new Set(prev); ids.forEach((id) => n.add(id)); return n; });
  const clearSelection = () => setSelected(new Set());
  const allVisibleSelected = visible.length > 0 && visible.every((i) => selected.has(i.id));
  const toggleAllVisible = () => (allVisibleSelected ? setSelected((prev) => { const n = new Set(prev); visible.forEach((i) => n.delete(i.id)); return n; }) : selectMany(visible.map((i) => i.id)));

  const startRun = async () => {
    setBusy(true);
    try {
      writeBoolPref(AI_PREF_KEY, aiEyes);
      await adminStartCulling(slug, { ai_eyes: aiEyes && Boolean(estimate?.ai_configured) });
      toast.info(aiEyes && estimate?.ai_configured ? 'Culling started — local pass first, then the AI eye check.' : 'Culling started — local sharpness + duplicate pass.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the culling pass.');
    } finally { setBusy(false); }
  };

  const clearRun = async () => {
    setBusy(true);
    try { await adminClearCulling(slug); await load(); toast.success('Culling results cleared.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not clear results.'); }
    finally { setBusy(false); }
  };

  const setHidden = async (hidden) => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const res = await adminSetImageVisibility(slug, { image_ids: [...selected], hidden });
      toast.success(hidden ? `${res.updated} photo${res.updated === 1 ? '' : 's'} hidden from clients.` : `${res.updated} photo${res.updated === 1 ? '' : 's'} visible to clients again.`);
      clearSelection();
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update visibility.'); }
    finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    setBusy(true);
    try {
      const res = await adminBulkDeleteImages(slug, { image_ids: [...selected] });
      toast.success(`${res.deleted} photo${res.deleted === 1 ? '' : 's'} deleted.`);
      clearSelection();
      setConfirmDelete(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete photos.'); }
    finally { setBusy(false); }
  };

  const hiddenSelected = selectedList.filter((i) => i.hidden).length;
  const canCompare = selected.size >= 2 && selected.size <= 4;

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-red-300" />
        <p className="text-sm text-red-200">{loadError}</p>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" className="border-[hsl(var(--lxra-border))]">Retry</Button>
          <Button asChild className="bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]"><Link to="/admin">Back to dashboard</Link></Button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] p-6 max-w-7xl mx-auto space-y-4"><Skeleton className="h-10 w-1/2 bg-[#141416]" /><Skeleton className="h-36 w-full bg-[#141416] rounded-2xl" /><div className="grid grid-cols-2 sm:grid-cols-4 gap-3"><SkeletonList count={8} className="aspect-[4/3] bg-[#141416] rounded-xl" /></div></div>;
  }

  const run = data.run;
  const summary = run?.summary;
  const galleryName = data.gallery?.name || data.gallery?.title || slug;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[hsl(var(--lxra-fg))] pb-28" data-testid={CULL.root}>
      <SEO title={`Cull — ${galleryName}`} description="LXRA Culling Assist review." />
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur border-b border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to="/admin" data-testid={CULL.backLink} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
          <div className="flex items-center gap-2">
            {run && (
              <Badge data-testid={CULL.statusBadge} variant="outline" className={`text-[10px] gap-1 ${run.status === 'done' ? 'border-emerald-400/40 text-emerald-400' : run.status === 'error' ? 'border-red-400/40 text-red-300' : 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]'}`}>
                {run.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}{run.status}
              </Badge>
            )}
            <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] hidden sm:inline">Culling Assist</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))] flex items-center gap-1.5"><Images className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> {slug}</p>
            <h1 data-testid={CULL.title} className="font-display text-3xl sm:text-4xl leading-none tracking-[0.02em] mt-2">{galleryName}</h1>
            <p className="text-sm text-[hsl(var(--lxra-muted))] mt-2">{images.length} photo{images.length === 1 ? '' : 's'}{data.hidden_count ? ` · ${data.hidden_count} hidden from clients` : ''}{run?.finished_at ? ` · last pass ${new Date(run.finished_at).toLocaleString()}` : ''}</p>
          </div>
          <Link to={`/galleries/${slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center h-9 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.08em] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors"><Eye className="h-3.5 w-3.5 mr-1.5" /> Client view</Link>
        </div>

        <CullRunPanel run={run} estimate={estimate} aiEyes={aiEyes} onAiEyesChange={setAiEyes} onRun={startRun} onClear={clearRun} busy={busy} imageCount={images.length} />

        {images.length === 0 ? (
          <div data-testid={CULL.empty} className="rounded-2xl border border-dashed border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
            <Images className="h-6 w-6 text-[hsl(var(--lxra-muted))] mx-auto" />
            <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))]">No photos in this gallery yet.</p>
            <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Upload photos from the Studio Dashboard, then come back to cull.</p>
          </div>
        ) : (
          <>
            {summary && (
              <div data-testid={CULL.summary} className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                {[
                  ['analyzed', 'Analyzed', summary.analyzed],
                  ['blurry', 'Blurry', summary.blurry],
                  ['eyes', 'Eyes closed', summary.eyes_closed],
                  ['expression', 'Expression', summary.expression],
                  ['posing', 'Posing', summary.posing],
                  ['groups', 'Duplicate groups', summary.duplicate_groups],
                  ['ai', 'AI checked', summary.ai_checked],
                ].map(([k, label, v]) => (
                  <div key={k} data-testid={CULL.summaryStat(k)} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">{label}</p>
                    <p className={`font-display text-2xl leading-none mt-1 ${v > 0 && ['blurry', 'eyes', 'expression', 'posing'].includes(k) ? 'text-red-300' : k === 'groups' && v > 0 ? 'text-[hsl(var(--lxra-gold))]' : ''}`}>{v ?? 0}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter photos">
                {FILTERS.map((f) => {
                  const active = filter === f.key;
                  return (
                    <button
                      key={f.key} type="button" role="tab" aria-selected={active} data-testid={CULL.filter(f.key)} onClick={() => setFilter(f.key)}
                      className={`h-8 rounded-full border px-3 text-[11px] uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))] ${active ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] font-semibold' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))]/60 hover:text-[hsl(var(--lxra-fg))]'}`}
                    >
                      {f.label} <span className={`ml-1 font-mono ${active ? 'text-[#0A0A0A]/70' : 'text-[hsl(var(--lxra-muted))]/70'}`}>{counts[f.key]}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" data-testid={CULL.selectAllVisible} onClick={toggleAllVisible} disabled={!visible.length} className="h-8 text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-[hsl(var(--lxra-fg))]">{allVisibleSelected ? 'Deselect shown' : `Select shown (${visible.length})`}</Button>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid={CULL.sortSelect} className="h-8 w-44 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                    <SelectItem value="order">Gallery order</SelectItem>
                    <SelectItem value="flagged">Flagged first</SelectItem>
                    <SelectItem value="sharpness_asc">Softest first</SelectItem>
                    <SelectItem value="sharpness_desc">Sharpest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filter === 'duplicates' && groups.length > 0 ? (
              <DuplicateGroups groups={groups} imagesById={imagesById} selectedIds={selected} onToggle={toggle} onSelectGroup={selectMany} onCompareGroup={setCompareIds} />
            ) : visible.length === 0 ? (
              <div data-testid={CULL.noResults} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
                <ScanEye className="h-6 w-6 text-[hsl(var(--lxra-muted))] mx-auto" />
                <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))]">{run ? 'Nothing matches this filter.' : 'Run a culling pass to see flags here.'}</p>
                {filter !== 'all' && <Button variant="ghost" size="sm" onClick={() => setFilter('all')} className="mt-2 text-xs text-[hsl(var(--lxra-gold))] hover:bg-[#1a1a1c]">Show all photos</Button>}
              </div>
            ) : (
              <CullGrid images={visible} selectedIds={selected} onToggle={toggle} />
            )}

            {!run && (
              <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Tip: nothing is ever deleted automatically. The pass only flags — you review, compare and decide.</p>
            )}
          </>
        )}
      </main>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div data-testid={CULL.selectionBar} className="fixed inset-x-0 bottom-0 z-50 border-t border-[hsl(var(--lxra-gold))]/40 bg-[#0A0A0A]/92 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            <p data-testid={CULL.selectionCount} className="text-xs text-[hsl(var(--lxra-fg))]"><span className="font-display text-xl text-[hsl(var(--lxra-gold))] mr-1.5">{selected.size}</span> selected{selectedList.filter(isFlagged).length ? ` · ${selectedList.filter(isFlagged).length} flagged` : ''}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" data-testid={CULL.compareButton} disabled={!canCompare} onClick={() => setCompareIds([...selected])} title={canCompare ? 'Compare side by side' : 'Select 2–4 photos to compare'} className="h-9 border-[hsl(var(--lxra-border))] bg-transparent text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-40"><GitCompareArrows className="mr-1.5 h-3.5 w-3.5" /> Compare</Button>
              {hiddenSelected < selected.size && (
                <Button size="sm" variant="outline" data-testid={CULL.hideButton} disabled={busy} onClick={() => setHidden(true)} className="h-9 border-[hsl(var(--lxra-border))] bg-transparent text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide from client</Button>
              )}
              {hiddenSelected > 0 && (
                <Button size="sm" variant="outline" data-testid={CULL.unhideButton} disabled={busy} onClick={() => setHidden(false)} className="h-9 border-[hsl(var(--lxra-border))] bg-transparent text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><Eye className="mr-1.5 h-3.5 w-3.5" /> Unhide</Button>
              )}
              <Button size="sm" data-testid={CULL.deleteButton} disabled={busy} onClick={() => setConfirmDelete(true)} className="h-9 bg-red-900/60 text-[11px] uppercase tracking-[0.08em] text-red-100 hover:bg-red-800/70 border border-red-400/30"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>
              <Button size="icon" variant="ghost" data-testid={CULL.clearSelection} onClick={clearSelection} aria-label="Clear selection" className="h-9 w-9 text-[hsl(var(--lxra-muted))] hover:bg-[#1a1a1c] hover:text-[hsl(var(--lxra-fg))]"><X className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      )}

      <CullCompare open={Boolean(compareIds)} onOpenChange={(v) => { if (!v) setCompareIds(null); }} images={compareList} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="border-[hsl(var(--lxra-border))] bg-[#0F0F10] text-[hsl(var(--lxra-fg))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl tracking-[0.03em]">Delete {selected.size} photo{selected.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[hsl(var(--lxra-muted))]">This removes them from the gallery for good, including any client favorites pointing at them. If you only want them out of the client&apos;s view, use <span className="text-[hsl(var(--lxra-fg))]">Hide from client</span> instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={CULL.deleteCancel} className="border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:bg-[#1a1a1c]">Keep them</AlertDialogCancel>
            <AlertDialogAction data-testid={CULL.deleteConfirm} onClick={(e) => { e.preventDefault(); deleteSelected(); }} disabled={busy} className="bg-red-900/70 text-red-100 hover:bg-red-800/80 border border-red-400/30">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete permanently'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
